import type { AppResource, ExperimentResource } from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { isInWork } from '../app/Archive.js';
import { api } from '../shell/api.js';
import { Failure } from '../shell/EmptyState.js';
import { useNavigate } from '../shell/router.js';
import { useAsync } from '../shell/useAsync.js';
import { Preflight, type PreflightSeed } from './Preflight.js';

/**
 * `Repeat for…`, where a direction is being read rather than on a separate page.
 *
 * One axis, three surfaces. R160 asks for it from an Experiment or a Candidate, R077
 * names the same thing `Adapt set` on a screenshot set, and R192 wants video Target
 * adaptation to go through it too - so this is one control mounted three times, not
 * three preflights. The mechanism is the Bulk Run preflight of task 14 exactly as it
 * stands: nothing new is asked of the server.
 *
 * The Brief is seeded here so the operator can read and edit what is being carried
 * before anything is priced. The Hypothesis and the explicitly selected shared
 * references travel with `sourceExperimentId` and are never retyped.
 */
export function RepeatFor({
  label,
  experimentId,
  candidateId = null,
  note,
}: {
  label: string;
  experimentId: string;
  /** The Candidate carried as a creative reference. Never a parent and never a copy. */
  candidateId?: string | null;
  note: string;
}): ReactNode {
  const [open, setOpen] = useState(false);

  return (
    <div className="repeat-for">
      <button
        type="button"
        className="secondary"
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        {label}
      </button>
      {open ? (
        <>
          <p className="note">{note}</p>
          <Destinations experimentId={experimentId} candidateId={candidateId} />
        </>
      ) : null}
    </div>
  );
}

interface Carried {
  readonly apps: readonly AppResource[];
  readonly experiment: ExperimentResource;
}

/** The matrix for one source. Opened on demand: nothing is read until it is asked for. */
function Destinations({
  experimentId,
  candidateId,
}: {
  experimentId: string;
  candidateId: string | null;
}): ReactNode {
  const navigate = useNavigate();
  const loaded = useAsync<Carried>(async () => {
    const [apps, detail] = await Promise.all([api.listApps(), api.readExperiment(experimentId)]);
    // The source App stays a destination even from the archive: it is the one already ticked.
    return { apps: apps.apps.filter((app) => isInWork(app) || app.id === detail.experiment.appId), experiment: detail.experiment };
  }, [experimentId]);

  if (loaded.error !== null) return <Failure message={loaded.error} onRetry={loaded.reload} />;
  if (loaded.value === null) return <p className="note" aria-busy="true" />;

  const { apps, experiment } = loaded.value;
  const seed: PreflightSeed = {
    operationKind: 'repeat',
    // The source App is ticked because the ordinary repeat is the same App in another
    // Target; every other App is one tick away and none of them is assumed.
    appIds: [experiment.appId],
    targets: [],
    assetTypes: [experiment.assetType],
    sharedBrief: experiment.brief,
    exceptions: {},
    sourceExperimentId: experimentId,
    sourceCandidateId: candidateId,
  };

  return <Preflight apps={apps} seed={seed} onLaunched={(runId) => navigate(`/bulk-runs/${runId}`)} />;
}
